import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '@/hooks/useApi';
import { usePagination } from '@/hooks/usePagination';
import { PageContainer } from '@/components/common/PageContainer';
import { LoadingSkeleton } from '@/components/common/LoadingSkeleton';
import { DataTable } from '@/components/common/DataTable';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Wallet, DollarSign, Package, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, Receipt, RotateCcw } from 'lucide-react';
import { formatCurrency, formatDate } from '@/lib/formatters';
import { cn } from '@/lib/utils';
import { ChartCard } from '@/components/features/dashboard/ChartCard';
import { RevenueTrendChart } from '@/components/features/charts/RevenueTrendChart';
import { useRevenueTrend } from '@/hooks/useDashboardAnalytics';
import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/contexts/I18nContext';
import { DriverCodSettlement, DriverPaySettlement, RefundSettlement } from '@/types/financial.types';

interface FinancialTransaction {
  id: number;
  shipment_id: number;
  tracking_number: string;
  transaction_type: 'service_fee' | 'cod_collection' | 'payment' | 'refund';
  amount: number;
  status: 'pending' | 'completed' | 'failed';
  created_at: string;
  description: string;
}

interface WalletBalance {
  services_wallet: number;
  cod_wallet: number;
  pending_cod: number;
  total_revenue: number;
  total_expenses: number;
}

export default function Finance() {
  const { user } = useAuth();
  const { t, tp } = useI18n();
  const navigate = useNavigate();
  const { get, loading } = useApi();
  const codStatsApi = useApi({ showErrorToast: false });
  const payStatsApi = useApi({ showErrorToast: false });
  const refundStatsApi = useApi({ showErrorToast: false });
  const pagination = usePagination();
  const { data: revenueTrend, loading: revenueTrendLoading } = useRevenueTrend(30);
  const [transactions, setTransactions] = useState<FinancialTransaction[]>([]);
  const [walletBalance, setWalletBalance] = useState<WalletBalance>({
    services_wallet: 0,
    cod_wallet: 0,
    pending_cod: 0,
    total_revenue: 0,
    total_expenses: 0,
  });
  const [codStats, setCodStats] = useState<{ total: number; count: number } | null>(null);
  const [payStats, setPayStats] = useState<{ total: number; count: number } | null>(null);
  const [refundStats, setRefundStats] = useState<{ total: number; count: number } | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);

  useEffect(() => {
    fetchFinancialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page]);

  useEffect(() => {
    fetchSettlementStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettlementStats = async () => {
    setStatsLoading(true);
    const extractList = (r: any): any[] => {
      const data = r?.success !== undefined ? r?.data : r;
      if (Array.isArray(data)) return data;
      return data?.list ?? data?.data ?? [];
    };
    const [codRes, payRes, refundRes] = await Promise.all([
      codStatsApi.get('/api/v1/driver-cod-settlements').catch(() => null),
      payStatsApi.get('/api/v1/driver-pay-settlements').catch(() => null),
      refundStatsApi.get('/api/v1/refunds').catch(() => null),
    ]);
    const codList: DriverCodSettlement[] = extractList(codRes);
    const codFiltered = codList.filter((s) => s.status === 'draft' || s.status === 'confirmed');
    setCodStats({
      total: codFiltered.reduce((s, r) => s + Number(r.total_amount || 0), 0),
      count: codFiltered.length,
    });

    const payList: DriverPaySettlement[] = extractList(payRes);
    const payFiltered = payList.filter((s) => s.status === 'draft' || s.status === 'confirmed');
    setPayStats({
      total: payFiltered.reduce((s, r) => s + Number(r.total_amount || 0), 0),
      count: payFiltered.length,
    });

    const refundList: RefundSettlement[] = extractList(refundRes);
    const refundFiltered = refundList.filter((s) => s.status === 'draft' || s.status === 'approved');
    setRefundStats({
      total: refundFiltered.reduce((s, r) => s + Number(r.amount || 0), 0),
      count: refundFiltered.length,
    });
    setStatsLoading(false);
  };

  const fetchFinancialData = async () => {
    try {
      // Fetch wallet balance
      const balanceResponse = await get('/api/v1/dashboard/finance/wallets');
      if (balanceResponse) {
        setWalletBalance(balanceResponse);
      }

      // Fetch transactions
      const transactionsResponse = await get('/api/v1/dashboard/finance/transactions', {
        page: pagination.page,
        limit: pagination.limit,
      });
      
      // Safely handle response
      if (transactionsResponse?.list && Array.isArray(transactionsResponse.list)) {
        setTransactions(transactionsResponse.list);
        pagination.updatePagination({
          total_pages: transactionsResponse.meta?.totalPages || 1,
          total: transactionsResponse.meta?.total || 0
        });
      } else {
        // Fallback to empty array if no data
        setTransactions([]);
        pagination.updatePagination({
          total_pages: 1,
          total: 0
        });
      }
    } catch (error) {
      console.error('Failed to fetch financial data:', error);
      // Set safe defaults on error
      setTransactions([]);
      pagination.updatePagination({
        total_pages: 1,
        total: 0
      });
    }
  };

  const getTransactionTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      service_fee: tp.finance.transactionTypeServiceFee,
      cod_collection: tp.finance.transactionTypeCodCollection,
      payment: tp.finance.transactionTypePayment,
      refund: tp.finance.transactionTypeRefund,
    };
    return labels[type] || type;
  };

  const getTransactionTypeIcon = (type: string) => {
    if (type === 'service_fee' || type === 'refund') {
      return <ArrowDownRight className="h-4 w-4 text-destructive" />;
    }
    return <ArrowUpRight className="h-4 w-4 text-green-500" />;
  };

  const columns = [
    {
      header: tp.finance.tableHeaderTrackingNumber,
      accessor: 'tracking_number' as keyof FinancialTransaction,
      className: 'font-medium',
    },
    {
      header: tp.finance.tableHeaderTransactionType,
      accessor: (row: FinancialTransaction) => (
        <div className="flex items-center gap-2">
          {getTransactionTypeIcon(row.transaction_type)}
          <span>{getTransactionTypeLabel(row.transaction_type)}</span>
        </div>
      ),
    },
    {
      header: tp.finance.tableHeaderAmount,
      accessor: (row: FinancialTransaction) => (
        <span className={cn(
          'font-semibold',
          row.transaction_type === 'service_fee' || row.transaction_type === 'refund' 
            ? 'text-destructive' 
            : 'text-green-500'
        )}>
          {formatCurrency(row.amount)}
        </span>
      ),
    },
    {
      header: tp.finance.tableHeaderStatus,
      accessor: (row: FinancialTransaction) => (
        <Badge variant={
          row.status === 'completed' ? 'default' :
          row.status === 'pending' ? 'secondary' :
          'destructive'
        }>
          {row.status === 'completed' ? tp.finance.statusCompleted :
           row.status === 'pending' ? tp.finance.statusPending :
           tp.finance.statusFailed}
        </Badge>
      ),
    },
    {
      header: tp.finance.tableHeaderDate,
      accessor: (row: FinancialTransaction) => formatDate(row.created_at),
      className: 'text-muted-foreground',
    },
    {
      header: tp.finance.tableHeaderDescription,
      accessor: 'description' as keyof FinancialTransaction,
      className: 'text-sm text-muted-foreground',
    },
  ];

  const netProfit = walletBalance.total_revenue - walletBalance.total_expenses;

  return (
    <PageContainer>
      <div className="space-y-4 md:space-y-6">
        <div>
          <h1 className="text-xl md:text-2xl lg:text-3xl font-bold mb-2">{tp.finance.pageTitle}</h1>
          <p className="text-sm md:text-base text-muted-foreground">{tp.finance.pageDescription}</p>
        </div>

        {/* Phase 2 settlement stats */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
          <Card
            role="button"
            onClick={() => navigate('/dashboard/driver-cod-settlements')}
            className="cursor-pointer hover:shadow-md transition-shadow"
          >
            <CardHeader className="pb-2 md:pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm md:text-base font-medium">{t.financials.unsettled_cod}</CardTitle>
                <div className="p-2 bg-blue-500/10 rounded-lg">
                  <Receipt className="h-4 w-4 md:h-5 md:w-5 text-blue-600" />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {statsLoading || !codStats ? (
                <Skeleton className="h-8 w-32" />
              ) : (
                <>
                  <div className="text-xl md:text-2xl font-bold text-blue-600">{formatCurrency(codStats.total)}</div>
                  <p className="text-xs md:text-sm text-muted-foreground mt-1">{codStats.count} {t.financials.cod_settlements}</p>
                </>
              )}
            </CardContent>
          </Card>

          <Card
            role="button"
            onClick={() => navigate('/dashboard/driver-pay-settlements')}
            className="cursor-pointer hover:shadow-md transition-shadow"
          >
            <CardHeader className="pb-2 md:pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm md:text-base font-medium">{t.financials.unsettled_pay}</CardTitle>
                <div className="p-2 bg-green-500/10 rounded-lg">
                  <Wallet className="h-4 w-4 md:h-5 md:w-5 text-green-600" />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {statsLoading || !payStats ? (
                <Skeleton className="h-8 w-32" />
              ) : (
                <>
                  <div className="text-xl md:text-2xl font-bold text-green-600">{formatCurrency(payStats.total)}</div>
                  <p className="text-xs md:text-sm text-muted-foreground mt-1">{payStats.count} {t.financials.pay_settlements}</p>
                </>
              )}
            </CardContent>
          </Card>

          <Card
            role="button"
            onClick={() => navigate('/dashboard/refunds')}
            className="cursor-pointer hover:shadow-md transition-shadow"
          >
            <CardHeader className="pb-2 md:pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm md:text-base font-medium">{t.financials.pending_refunds}</CardTitle>
                <div className="p-2 bg-red-500/10 rounded-lg">
                  <RotateCcw className="h-4 w-4 md:h-5 md:w-5 text-red-600" />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {statsLoading || !refundStats ? (
                <Skeleton className="h-8 w-32" />
              ) : (
                <>
                  <div className="text-xl md:text-2xl font-bold text-red-600">{formatCurrency(refundStats.total)}</div>
                  <p className="text-xs md:text-sm text-muted-foreground mt-1">{refundStats.count} {t.financials.refunds}</p>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Wallets Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
          {/* Services Wallet */}
          <Card className="relative overflow-hidden">
            <CardHeader className="pb-2 md:pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm md:text-base font-medium">{tp.finance.walletServices}</CardTitle>
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Wallet className="h-4 w-4 md:h-5 md:w-5 text-primary" />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-xl md:text-2xl font-bold">{formatCurrency(walletBalance.services_wallet)}</div>
              <p className="text-xs md:text-sm text-muted-foreground mt-1">{tp.finance.walletServicesSubtitle}</p>
            </CardContent>
          </Card>

          {/* COD Wallet */}
          <Card className="relative overflow-hidden">
            <CardHeader className="pb-2 md:pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm md:text-base font-medium">{tp.finance.walletCod}</CardTitle>
                <div className="p-2 bg-green-500/10 rounded-lg">
                  <DollarSign className="h-4 w-4 md:h-5 md:w-5 text-green-500" />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-xl md:text-2xl font-bold text-green-500">{formatCurrency(walletBalance.cod_wallet)}</div>
              <p className="text-xs md:text-sm text-muted-foreground mt-1">{tp.finance.walletCodSubtitle}</p>
            </CardContent>
          </Card>

          {/* Pending COD */}
          <Card className="relative overflow-hidden">
            <CardHeader className="pb-2 md:pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm md:text-base font-medium">{tp.finance.pendingCod}</CardTitle>
                <div className="p-2 bg-orange-500/10 rounded-lg">
                  <Package className="h-4 w-4 md:h-5 md:w-5 text-orange-500" />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-xl md:text-2xl font-bold text-orange-500">{formatCurrency(walletBalance.pending_cod)}</div>
              <p className="text-xs md:text-sm text-muted-foreground mt-1">{tp.finance.pendingCodSubtitle}</p>
            </CardContent>
          </Card>

          {/* Net Profit */}
          <Card className="relative overflow-hidden">
            <CardHeader className="pb-2 md:pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-sm md:text-base font-medium">{tp.finance.netProfit}</CardTitle>
                <div className={cn(
                  "p-2 rounded-lg",
                  netProfit >= 0 ? "bg-green-500/10" : "bg-destructive/10"
                )}>
                  {netProfit >= 0 ? (
                    <TrendingUp className="h-4 w-4 md:h-5 md:w-5 text-green-500" />
                  ) : (
                    <TrendingDown className="h-4 w-4 md:h-5 md:w-5 text-destructive" />
                  )}
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className={cn(
                "text-xl md:text-2xl font-bold",
                netProfit >= 0 ? "text-green-500" : "text-destructive"
              )}>
                {formatCurrency(netProfit)}
              </div>
              <p className="text-xs md:text-sm text-muted-foreground mt-1">
                {tp.finance.netProfitSubtitle}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Revenue Trend Chart — admin and carrier only */}
        {user?.role !== 'tenant' && (
          <ChartCard title={tp.finance.chartTitle} loading={revenueTrendLoading}>
            <RevenueTrendChart
              data={revenueTrend}
              showPlatformFees={user?.role === 'admin'}
            />
          </ChartCard>
        )}

        {/* Transactions Table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base md:text-lg">{tp.finance.transactionsTableTitle}</CardTitle>
            <CardDescription className="text-sm">{tp.finance.transactionsTableDescription}</CardDescription>
          </CardHeader>
          <CardContent className="p-0 md:p-6">
            {loading ? (
              <div className="p-4 md:p-6">
                <LoadingSkeleton type="table" rows={8} />
              </div>
            ) : transactions.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <p className="text-lg font-medium mb-2">{tp.finance.noTransactionsMessage}</p>
                <p className="text-sm">{tp.finance.noTransactionsDescription}</p>
              </div>
            ) : (
              <DataTable
                data={transactions}
                columns={columns}
                pagination={{
                  currentPage: pagination.page,
                  totalPages: pagination.totalPages,
                  onPageChange: pagination.goToPage,
                }}
                emptyMessage={tp.finance.noTransactionsMessage}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </PageContainer>
  );
}
